import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { ShoppingBag } from "lucide-react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

interface Banner {
  id: number;
  name: string;
  imageUrl: string;
  category: string;
  source?: "default" | "shop";
}

interface ShopBanner {
  id: number;
  name: string;
  imageUrl: string;
  itemType: "banner";
}

interface ProfileData {
  user: {
    id: number;
    username: string;
    createdAt: string;
  };
  profile: {
    bannerUrl?: string | null;
    bannerId?: number | null;
    bannerSource?: string;
    status?: string;
  } | null;
}

interface BannerGridProps {
  readonly banners: Banner[];
  readonly label: string;
  readonly selectedBanner: number | null;
  readonly bannerType: "default" | "shop";
  readonly onSelectBanner: (bannerId: number, source: "default" | "shop") => void;
}

function BannerGrid({ banners, label, selectedBanner, bannerType, onSelectBanner }: Readonly<BannerGridProps>) {
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium text-muted-foreground">{label}</h3>
      <div className="grid grid-cols-2 gap-3">
        {banners?.map((banner: Banner) => (
          <motion.div
            key={`${bannerType}-${banner.id}`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Card
              className={cn(
                "cursor-pointer overflow-hidden transition-all duration-200",
                "hover:border-primary/30",
                selectedBanner === banner.id
                  ? "border-4 border-primary"
                  : "border border-white/10"
              )}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onSelectBanner(banner.id, bannerType);
              }}
            >
              <CardContent className="p-3">
                <div className="aspect-[2/1] rounded-lg overflow-hidden bg-black/40">
                  <img
                    src={banner.imageUrl}
                    alt={banner.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

export function BannerSelect({ onClose }: { onClose?: () => void }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Separate states for each banner type
  const [selectedFreeBanner, setSelectedFreeBanner] = useState<number | null>(null);
  const [selectedShopBanner, setSelectedShopBanner] = useState<number | null>(null);
  
  // Track which type of banner is currently selected
  const [activeBannerType, setActiveBannerType] = useState<"default" | "shop" | null>(null);

  // Fetch user's purchased banners
  const { data: purchasedBanners } = useQuery({
    queryKey: ["myBanners"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/customization/myBanners");
      if (!res.ok) throw new Error("Failed to fetch purchased banners");
      const data = await res.json();
      return data.map((banner: any) => ({
        ...banner,
        source: "shop",
        category: banner.category || "purchased"
      }));
    },
  });

  // Fetch free banners
  const { data: freeBanners } = useQuery({
    queryKey: ["freeBanners"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/customization/banners");
      if (!res.ok) throw new Error("Failed to fetch free banners");
      const data = await res.json();
      return data.map((banner: any) => ({ ...banner, source: "default" }));
    },
  });

  // Fetch current profile
  const { data: currentProfile } = useQuery({
    queryKey: ["profile"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/profile/current");
      if (!res.ok) throw new Error("Failed to fetch profile");
      return res.json() as Promise<ProfileData>;
    },
  });

  // Set initial selected banner
  useEffect(() => {
    if (currentProfile?.profile?.bannerId) {
      const bannerId = currentProfile.profile.bannerId;
      const bannerSource = currentProfile.profile.bannerSource as "default" | "shop" || "default";
      
      if (bannerSource === "shop") {
        setSelectedShopBanner(bannerId);
        setActiveBannerType("shop");
      } else {
        setSelectedFreeBanner(bannerId);
        setActiveBannerType("default");
      }
    }
  }, [currentProfile]);

  // Separate handlers for each banner type
  const handleSelectFreeBanner = (bannerId: number) => {
    setSelectedFreeBanner(bannerId);
    setActiveBannerType("default");
  };

  const handleSelectShopBanner = (bannerId: number) => {
    setSelectedShopBanner(bannerId);
    setActiveBannerType("shop");
  };

  // Handle banner selection based on type
  const handleBannerSelect = (bannerId: number, source: "default" | "shop") => {
    if (source === "default") {
      handleSelectFreeBanner(bannerId);
    } else {
      handleSelectShopBanner(bannerId);
    }
  };

  // Mutation to set banner
  const setBannerMutation = useMutation({
    mutationFn: async (data: { bannerId: number; source: "default" | "shop" }) => {
      const res = await apiRequest("POST", "/api/profile/set-banner", {
        bannerId: data.bannerId,
        source: data.source
      });
      return res;
    },
    onSuccess: () => {
      toast({
        title: "Banner updated",
        description: "Your profile banner has been updated successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      if (onClose) {
        onClose();
      }
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update banner. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Separate save handlers
  const handleSaveFreeBanner = () => {
    if (selectedFreeBanner !== null) {
      setBannerMutation.mutate({ 
        bannerId: selectedFreeBanner, 
        source: "default" 
      });
    }
  };

  const handleSaveShopBanner = () => {
    if (selectedShopBanner !== null) {
      setBannerMutation.mutate({ 
        bannerId: selectedShopBanner, 
        source: "shop" 
      });
    }
  };

  const navigateToShop = () => {
    window.location.href = "/shop";
  };

  return (
    <div className="space-y-4">
      {purchasedBanners?.length > 0 && (
        <div className={cn("transition-opacity duration-200", 
          activeBannerType === "shop" || activeBannerType === null ? "opacity-100" : "opacity-50"
        )}>
          <BannerGrid
            banners={purchasedBanners}
            label="Purchased Banners"
            selectedBanner={selectedShopBanner}
            bannerType="shop"
            onSelectBanner={handleBannerSelect}
          />
          
          {activeBannerType === "shop" && selectedShopBanner !== null && (
            <div className="mt-2">
              <Button 
                className="w-full"
                onClick={handleSaveShopBanner}
                disabled={setBannerMutation.isPending}
              >
                {setBannerMutation.isPending ? "Saving..." : "Use This Purchased Banner"}
              </Button>
            </div>
          )}
        </div>
      )}

      <div className="pb-4"></div>
      
      {freeBanners?.length > 0 && (
        <div className={cn("transition-opacity duration-200", 
          activeBannerType === "default" || activeBannerType === null ? "opacity-100" : "opacity-50"
        )}>
          <BannerGrid
            banners={freeBanners}
            label="Free Banners"
            selectedBanner={selectedFreeBanner}
            bannerType="default"
            onSelectBanner={handleBannerSelect}
          />
          
          {activeBannerType === "default" && selectedFreeBanner !== null && (
            <div className="mt-2">
              <Button 
                className="w-full"
                onClick={handleSaveFreeBanner}
                disabled={setBannerMutation.isPending}
              >
                {setBannerMutation.isPending ? "Saving..." : "Use This Free Banner"}
              </Button>
            </div>
          )}
        </div>
      )}

      <div className="flex gap-2 pt-8">
        <Button
          variant="outline"
          className="flex-1 border-primary/20"
          onClick={navigateToShop}
        >
          <ShoppingBag className="w-4 h-4 mr-2" />
          Browse Shop
        </Button>
      </div>
    </div>
  );
}